import React, { useState, useMemo } from 'react';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  Squares2X2Icon,
  ListBulletIcon,
  ArchiveBoxIcon,
  StarIcon,
  FolderIcon,
  TagIcon,
  BookOpenIcon,
  XMarkIcon,
  Bars3Icon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useRichNotes } from '../hooks/useRichNotes';
import { useLanguage } from '../contexts/LanguageContext';
import LoadingScreen from '../components/common/LoadingScreen';
import CommonPageHeader from '../components/common/CommonPageHeader';
import RichNoteEditor from '../components/richNotes/RichNoteEditor';
import RichNoteCard from '../components/richNotes/RichNoteCard';
import RichNoteViewer from '../components/richNotes/RichNoteViewer';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { RichNote } from '../types/richNotes';

const RichNotes: React.FC = () => {
  const { t } = useLanguage();
  const {
    notes,
    allNotes,
    loading,
    filters,
    setFilters,
    categories,
    allTags,
    createNote,
    updateNote,
    deleteNote,
    archiveNote,
    togglePin,
    toggleFavorite,
  } = useRichNotes();

  const [showEditor, setShowEditor] = useState(false);
  const [showDocumentForm, setShowDocumentForm] = useState(false);
  const [showDocumentGenerator, setShowDocumentGenerator] = useState(false);
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editingNote, setEditingNote] = useState<RichNote | null>(null);
  const [viewingNote, setViewingNote] = useState<RichNote | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<RichNote | null>(null);
  const [noteToArchive, setNoteToArchive] = useState<RichNote | null>(null);
  const [customCategories, setCustomCategories] = useState<string[]>([]);
  const [editingCategory, setEditingCategory] = useState<string | null>(null);
  const [editCategoryName, setEditCategoryName] = useState('');

  // Handle create note
  const handleCreateNote = (presetCategory?: string) => {
    setEditingNote(null);
    // If a category is preset, we'll pass it to the editor
    if (presetCategory) {
      // Store the preset category temporarily
      setFilters({ ...filters, category: presetCategory });
    }
    setShowEditor(true);
  };

  // Handle edit note
  const handleEditNote = (note: RichNote) => {
    setEditingNote(note);
    setViewingNote(null);
    setShowEditor(true);
  };

  // Handle save note
  const handleSaveNote = async (title: string, content: string, plainText: string, category: string, tags: string[], color: string) => {
    if (editingNote) {
      // Update existing note
      await updateNote(editingNote.id, {
        title,
        content,
        plainText,
        category,
        tags,
        color: color as any,
      });
    } else {
      // Create new note
      await createNote({
        title,
        content,
        plainText,
        category,
        tags,
        color: color as any,
      });
    }
    setShowEditor(false);
    setEditingNote(null);
  };

  // Handle view note
  const handleViewNote = (note: RichNote) => {
    setViewingNote(note);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (noteToDelete) {
      await deleteNote(noteToDelete.id);
      setNoteToDelete(null);
      if (viewingNote?.id === noteToDelete.id) {
        setViewingNote(null);
      }
    }
  };

  // Handle archive confirmation
  const handleArchiveConfirm = async () => {
    if (noteToArchive) {
      await archiveNote(noteToArchive.id);
      setNoteToArchive(null);
      if (viewingNote?.id === noteToArchive.id) {
        setViewingNote(null);
      }
    }
  };

  // Merge categories from notes with custom categories
  const allCategories = useMemo(() => {
    const existingCategories = categories.map(c => c.name);
    const uniqueSet = new Set([...customCategories, ...existingCategories]);
    const merged = Array.from(uniqueSet);
    
    return merged.map(name => {
      const existing = categories.find(c => c.name === name);
      return existing || { id: name, name, count: 0, color: 'blue' };
    });
  }, [categories, customCategories]);

  // Statistics - always show total count regardless of filters
  const stats = useMemo(() => {
    const nonArchivedNotes = allNotes.filter(n => !n.isArchived);
    return {
      total: nonArchivedNotes.length, // All non-archived notes
      pinned: nonArchivedNotes.filter(n => n.isPinned).length,
      favorites: nonArchivedNotes.filter(n => n.isFavorite).length,
      categories: allCategories.length,
    };
  }, [allNotes, allCategories]);

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="h-full flex flex-col">
      <CommonPageHeader
        title="Jegyzetfüzet"
        subtitle="Belső jegyzetek, leírások és dokumentáció"
        icon={BookOpenIcon}
      >
        <div className="flex items-center gap-2">
          {/* Mobile hamburger menu */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2.5 text-white hover:bg-white/20 rounded-xl transition-all duration-200 hover:scale-105"
          >
            {isMobileMenuOpen ? (
              <XMarkIcon className="h-5 w-5" />
            ) : (
              <Bars3Icon className="h-5 w-5" />
            )}
          </button>

          {/* View mode toggle - hidden on mobile */}
          <div className="hidden md:flex items-center bg-white/20 backdrop-blur-md rounded-xl p-1 shadow-md border border-white/30">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-all duration-200 ${
                viewMode === 'grid'
                  ? 'bg-white/30 text-white shadow-sm'
                  : 'text-white/80 hover:text-white hover:bg-white/20'
              }`}
              title="Rács nézet"
            >
              <Squares2X2Icon className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-all duration-200 ${
                viewMode === 'list'
                  ? 'bg-white/30 text-white shadow-sm'
                  : 'text-white/80 hover:text-white hover:bg-white/20'
              }`}
              title="Lista nézet"
            >
              <ListBulletIcon className="h-4 w-4" />
            </button>
          </div>

          {/* New note button */}
          <button
            onClick={() => handleCreateNote()}
            className="group relative flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 hover:from-indigo-600 hover:via-purple-600 hover:to-pink-600 rounded-xl transition-all duration-300 shadow-lg hover:shadow-2xl hover:scale-105 overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
            <PlusIcon className="h-4 w-4 relative z-10" />
            <span className="hidden sm:inline relative z-10">Új jegyzet</span>
            <span className="sm:hidden relative z-10">Új</span>
          </button>
        </div>
      </CommonPageHeader>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <div className="flex-1 overflow-hidden bg-gray-50 dark:bg-gray-900">
        <div className="h-full flex">
          {/* Sidebar with filters and categories */}
          <div className={`
            ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
            md:translate-x-0 transition-transform duration-300 ease-in-out
            fixed md:relative z-50 md:z-auto
            w-72 sm:w-80 md:w-64 h-full md:h-auto flex-shrink-0 
            bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 overflow-y-auto
          `}>
            <div className="p-3">
              {/* Mobile close button */}
              <div className="md:hidden flex justify-between items-center mb-4 p-2">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Szűrők
                </h3>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              {/* Quick filters */}
              <div>
                <h3 className="text-xs font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  Gyors szűrők
                </h3>
                <div className="space-y-0.5">
                  <button
                    onClick={() => {
                      setFilters({ ...filters, showPinned: !filters.showPinned, showFavorites: false, showArchived: false });
                      setIsMobileMenuOpen(false);
                    }}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                      filters.showPinned
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-l-2 border-blue-500'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    📌 <span>Rögzített</span>
                    <span className="ml-auto text-xs">{stats.pinned}</span>
                  </button>
                  <button
                    onClick={() => {
                      setFilters({ ...filters, showFavorites: !filters.showFavorites, showPinned: false, showArchived: false });
                      setIsMobileMenuOpen(false);
                    }}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                      filters.showFavorites
                        ? 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300 border-l-2 border-yellow-500'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    <StarIcon className="w-4 h-4" />
                    <span>Kedvencek</span>
                    <span className="ml-auto text-xs">{stats.favorites}</span>
                  </button>
                  <button
                    onClick={() => {
                      setFilters({ ...filters, showArchived: !filters.showArchived, showPinned: false, showFavorites: false });
                      setIsMobileMenuOpen(false);
                    }}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                      filters.showArchived
                        ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-l-2 border-gray-500'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    <ArchiveBoxIcon className="w-4 h-4" />
                    <span>Archívum</span>
                  </button>
                </div>
              </div>

              {/* Categories */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <FolderIcon className="w-3 h-3" />
                    Kategóriák
                  </h3>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowNewCategoryInput(!showNewCategoryInput);
                    }}
                    className="p-1 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                    title="Új kategória"
                  >
                    <PlusIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  </button>
                </div>

                {/* New category input */}
                {showNewCategoryInput && (
                  <div className="mb-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <div className="flex gap-1">
                      <input
                        type="text"
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        placeholder="Kategória neve..."
                        className="flex-1 px-2 py-1 text-xs border border-blue-300 dark:border-blue-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && newCategoryName.trim()) {
                            e.preventDefault();
                            const categoryName = newCategoryName.trim();
                            // Add to custom categories
                            if (!customCategories.includes(categoryName)) {
                              setCustomCategories([...customCategories, categoryName]);
                            }
                            // Set the filter to show this category
                            setFilters({ ...filters, category: categoryName });
                            setNewCategoryName('');
                            setShowNewCategoryInput(false);
                          }
                        }}
                        autoFocus
                      />
                      <button
                        onClick={() => {
                          if (newCategoryName.trim()) {
                            const categoryName = newCategoryName.trim();
                            // Add to custom categories
                            if (!customCategories.includes(categoryName)) {
                              setCustomCategories([...customCategories, categoryName]);
                            }
                            // Set the filter to show this category
                            setFilters({ ...filters, category: categoryName });
                            setNewCategoryName('');
                            setShowNewCategoryInput(false);
                          }
                        }}
                        className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-md transition-colors"
                        title="Kategória létrehozása"
                      >
                        ✓
                      </button>
                      <button
                        onClick={() => {
                          setShowNewCategoryInput(false);
                          setNewCategoryName('');
                        }}
                        className="px-2 py-1 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 text-xs"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                )}

                <div className="space-y-0.5">
                  <button
                    onClick={() => {
                      setFilters({ ...filters, category: '' });
                      setIsMobileMenuOpen(false);
                    }}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                      !filters.category
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-l-2 border-blue-500'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    <span>Összes</span>
                    <span className="ml-auto text-xs">{stats.total}</span>
                  </button>
                  {allCategories.map((category) => (
                    <div key={category.id} className="relative group">
                      {editingCategory === category.name ? (
                        <div className="flex gap-1 px-2 py-1">
                          <input
                            type="text"
                            value={editCategoryName}
                            onChange={(e) => setEditCategoryName(e.target.value)}
                            onKeyPress={async (e) => {
                              if (e.key === 'Enter' && editCategoryName.trim()) {
                                e.preventDefault();
                                const oldName = category.name;
                                const newName = editCategoryName.trim();
                                
                                // Update all notes with this category
                                const notesToUpdate = allNotes.filter(n => n.category === oldName);
                                for (const note of notesToUpdate) {
                                  await updateNote(note.id, { category: newName });
                                }
                                
                                // Update custom categories
                                setCustomCategories(prev => 
                                  prev.map(c => c === oldName ? newName : c)
                                );
                                
                                setEditingCategory(null);
                                setEditCategoryName('');
                              }
                            }}
                            className="flex-1 px-2 py-1 text-xs border border-blue-300 dark:border-blue-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                            autoFocus
                          />
                          <button
                            onClick={async () => {
                              if (editCategoryName.trim()) {
                                const oldName = category.name;
                                const newName = editCategoryName.trim();
                                
                                // Update all notes with this category
                                const notesToUpdate = allNotes.filter(n => n.category === oldName);
                                for (const note of notesToUpdate) {
                                  await updateNote(note.id, { category: newName });
                                }
                                
                                // Update custom categories
                                setCustomCategories(prev => 
                                  prev.map(c => c === oldName ? newName : c)
                                );
                                
                                setEditingCategory(null);
                                setEditCategoryName('');
                              }
                            }}
                            className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-md"
                          >
                            ✓
                          </button>
                          <button
                            onClick={() => {
                              setEditingCategory(null);
                              setEditCategoryName('');
                            }}
                            className="px-2 py-1 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 text-xs"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <div
                          onClick={() => {
                            setFilters({ ...filters, category: category.name });
                            setIsMobileMenuOpen(false);
                          }}
                          className={`w-full flex items-center gap-1 px-3 py-2 rounded-md text-xs font-medium transition-colors cursor-pointer ${
                            filters.category === category.name
                              ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-l-2 border-blue-500'
                              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                          }`}
                        >
                          <span className="truncate text-left">{category.name}</span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingCategory(category.name);
                              setEditCategoryName(category.name);
                            }}
                            className="p-0.5 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Kategória átnevezése"
                          >
                            <PencilIcon className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                          </button>
                          {category.count === 0 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setCustomCategories(prev => prev.filter(c => c !== category.name));
                              }}
                              className="p-0.5 hover:bg-red-100 dark:hover:bg-red-900/30 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Kategória törlése"
                            >
                              <TrashIcon className="w-3 h-3 text-red-600 dark:text-red-400" />
                            </button>
                          )}
                          <span className="ml-auto text-xs">{category.count}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Tags */}
              {allTags.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-2">
                    <TagIcon className="w-3 h-3" />
                    Címkék
                  </h3>
                  <div className="flex flex-wrap gap-1.5">
                    {allTags.map((tag) => (
                      <button
                        key={tag}
                        onClick={() => {
                          const newTags = filters.tags.includes(tag)
                            ? filters.tags.filter(t => t !== tag)
                            : [...filters.tags, tag];
                          setFilters({ ...filters, tags: newTags });
                        }}
                        className={`px-2 py-1 rounded-full text-xs transition-colors ${
                          filters.tags.includes(tag)
                            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Main content area */}
          <div className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-6">
            {/* Mobile search */}
            <div className="sm:hidden mb-4">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Keresés a jegyzetekben..."
                  value={filters.searchQuery}
                  onChange={(e) => setFilters({ ...filters, searchQuery: e.target.value })}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Statistics bar */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-gray-200 dark:border-gray-700">
                <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.total}</div>
                <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Összes</div>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-gray-200 dark:border-gray-700">
                <div className="text-xl sm:text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.pinned}</div>
                <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Rögzített</div>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-gray-200 dark:border-gray-700">
                <div className="text-xl sm:text-2xl font-bold text-yellow-600 dark:text-yellow-400">{stats.favorites}</div>
                <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Kedvenc</div>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-gray-200 dark:border-gray-700">
                <div className="text-xl sm:text-2xl font-bold text-purple-600 dark:text-purple-400">{stats.categories}</div>
                <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Kategória</div>
              </div>
            </div>

            {/* Notes grid/list */}
            {notes.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-500 dark:text-gray-400">
                <BookOpenIcon className="w-16 h-16 mb-4 text-gray-300 dark:text-gray-600" />
                <h3 className="text-xl font-medium mb-2">
                  {filters.searchQuery || filters.category || filters.tags.length > 0
                    ? 'Nincs találat'
                    : 'Még nincsenek jegyzeteid'}
                </h3>
                <p className="text-center max-w-md mb-6">
                  {filters.searchQuery || filters.category || filters.tags.length > 0
                    ? 'Próbálj meg más keresési feltételeket használni'
                    : 'Hozd létre az első jegyzetedet és kezdj el dokumentálni!'}
                </p>
                {!filters.searchQuery && !filters.category && filters.tags.length === 0 && (
                  <button
                    onClick={() => handleCreateNote()}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors shadow-sm"
                  >
                    <PlusIcon className="h-5 w-5" />
                    <span>Első jegyzet létrehozása</span>
                  </button>
                )}
              </div>
            ) : (
              <div className={
                viewMode === 'grid'
                  ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4'
                  : 'space-y-3'
              }>
                {notes.map((note) => (
                  <RichNoteCard
                    key={note.id}
                    note={note}
                    onEdit={handleEditNote}
                    onDelete={(noteId) => {
                      const note = notes.find(n => n.id === noteId);
                      if (note) setNoteToDelete(note);
                    }}
                    onToggleFavorite={toggleFavorite}
                    onTogglePin={togglePin}
                    onArchive={(noteId) => {
                      const note = notes.find(n => n.id === noteId);
                      if (note) setNoteToArchive(note);
                    }}
                    onClick={handleViewNote}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Editor Modal */}
      {showEditor && (
        <RichNoteEditor
          initialTitle={editingNote?.title}
          initialContent={editingNote?.content}
          initialCategory={editingNote?.category || filters.category || 'Általános'}
          initialTags={editingNote?.tags}
          initialColor={editingNote?.color}
          categories={allCategories.map(c => c.name)}
          onSave={handleSaveNote}
          onCancel={() => {
            setShowEditor(false);
            setEditingNote(null);
          }}
          isEditing={!!editingNote}
        />
      )}

      {/* Viewer Modal */}
      {viewingNote && (
        <RichNoteViewer
          note={viewingNote}
          onClose={() => setViewingNote(null)}
          onEdit={handleEditNote}
          onDelete={(noteId) => {
            const note = notes.find(n => n.id === noteId);
            if (note) setNoteToDelete(note);
          }}
          onToggleFavorite={toggleFavorite}
          onArchive={(noteId) => {
            const note = notes.find(n => n.id === noteId);
            if (note) setNoteToArchive(note);
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!noteToDelete}
        onClose={() => setNoteToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Jegyzet törlése"
        message="Biztosan törölni szeretnéd ezt a jegyzetet? Ez a művelet nem vonható vissza."
        itemName={noteToDelete?.title}
        confirmText="Törlés"
        cancelText="Mégse"
        type="danger"
      />

      {/* Archive Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!noteToArchive}
        onClose={() => setNoteToArchive(null)}
        onConfirm={handleArchiveConfirm}
        title="Jegyzet archiválása"
        message="Biztosan archiválni szeretnéd ezt a jegyzetet? Az archivált jegyzeteket később visszaállíthatod."
        itemName={noteToArchive?.title}
        confirmText="Archiválás"
        cancelText="Mégse"
        type="warning"
      />
    </div>
  );
};

export default RichNotes;
